import React, { useState, useEffect } from "react";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import { api, appUserAuth } from "../lib/api";
import { toast } from "sonner";
import { EnvelopeSimple, Phone, ChatCircleText, ShieldCheck, Clock, MapPin, ArrowRight, CalendarBlank, CheckCircle } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

const PHONE_DISPLAY = "678-834-2571";
const PHONE_TEL = "+16788342571";

const TIME_SLOTS = [
  { v: "morning", label: "Morning (9am – 12pm ET)" },
  { v: "afternoon", label: "Afternoon (12pm – 5pm ET)" },
  { v: "evening", label: "Evening (5pm – 8pm ET)" },
];

function _formatDateLabel(d) {
  return d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" });
}
function _nextBusinessDates(count = 10) {
  const out = []; const today = new Date(); today.setHours(0, 0, 0, 0);
  let d = new Date(today); d.setDate(d.getDate() + 1);
  while (out.length < count) {
    const day = d.getDay();
    if (day !== 0 && day !== 6) out.push(new Date(d));
    d.setDate(d.getDate() + 1);
  }
  return out;
}

export default function Support() {
  const emailPrefill = appUserAuth.email() || "";
  const namePrefill = appUserAuth.fullName() || "";
  const [dates] = useState(_nextBusinessDates(10));
  const [form, setForm] = useState({
    full_name: namePrefill, email: emailPrefill, phone: "",
    preferred_date: "", preferred_time_slot: "morning", topic: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  useEffect(() => {
    setForm((f) => ({ ...f, preferred_date: dates[0].toISOString().slice(0, 10) }));
  }, [dates]);

  const submitCallback = async () => {
    if (!form.full_name || !form.email || !form.phone) { toast.error("Name, email and phone are required."); return; }
    setSubmitting(true);
    try {
      const r = await api.post("/support/callback-request", form);
      setSubmitted(r.data?.message || "You're on the list.");
      toast.success("Callback booked.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't book. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div data-testid="support-page" className="min-h-screen bg-exodus-bg text-exodus-text">
      <Nav />
      <section className="max-w-5xl mx-auto px-6 lg:px-12 pt-36 pb-16">
        <p className="eyebrow"><span className="gold-divider" />Support &amp; Contact</p>
        <h1 data-testid="support-headline" className="font-display text-5xl lg:text-6xl mt-4 leading-[0.95]">
          We&apos;re here <span className="gold-text italic">to help.</span>
        </h1>
        <p className="text-lg text-exodus-muted mt-6 max-w-2xl leading-relaxed">
          Questions about the app, your account, your PIN, calculator projections, saved scenarios, uploaded policies, or booking a private consult? Reach us using any of the channels below. We reply to every message.
        </p>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-12 pb-16">
        <div className="grid md:grid-cols-2 gap-6">
          <ContactCard testid="support-email" icon={<EnvelopeSimple size={24} weight="thin" />} title="Email"
            primary="support@exoduslifeandwealth.com" href="mailto:support@exoduslifeandwealth.com"
            subtitle="We reply within one business day. Include your registered email and a short description of the issue." />
          <ContactCard testid="support-phone" icon={<Phone size={24} weight="thin" />} title="Phone"
            primary={PHONE_DISPLAY} href={`tel:${PHONE_TEL}`}
            subtitle="Tap to call. Or book a callback below at a time that works for you." />
          <ContactCard testid="support-reserve" icon={<ChatCircleText size={24} weight="thin" />} title="Book a private consult"
            primary="Reserve a session" href="/reserve" internal
            subtitle="30-minute private call with a licensed advisor. $100 refundable deposit so only serious inquiries book." />
          <ContactCard testid="support-privacy" icon={<ShieldCheck size={24} weight="thin" />} title="Privacy &amp; data"
            primary="Privacy policy" href="/privacy" internal
            subtitle="How we handle your name, email, and app activity. Full data-use policy." />
        </div>
      </section>

      <section id="callback" className="max-w-5xl mx-auto px-6 lg:px-12 pb-16">
        <div className="card-surface p-8 lg:p-10">
          <div className="flex items-center gap-3 mb-2">
            <CalendarBlank size={20} className="text-exodus-gold" />
            <p className="eyebrow">Free callback booking</p>
          </div>
          <h2 className="font-display text-3xl mt-2">Get a call at your time — no deposit required.</h2>
          <p className="text-sm text-exodus-muted mt-3 max-w-2xl leading-relaxed">
            Pick a date and window that works for you. Leon will personally call you at the number you provide during that window. Free, no obligation.
          </p>
          {submitted ? (
            <div data-testid="callback-success" className="mt-8 flex items-start gap-4 p-6 border border-emerald-500/40 bg-emerald-500/10">
              <CheckCircle size={28} weight="fill" className="text-emerald-400 shrink-0" />
              <div>
                <p className="font-display text-xl">You&apos;re on the list.</p>
                <p className="text-sm text-exodus-muted mt-2">{submitted}</p>
                <p className="text-xs text-exodus-muted mt-4">Need us sooner? Call directly at <a href={`tel:${PHONE_TEL}`} className="gold-text hover:underline">{PHONE_DISPLAY}</a></p>
              </div>
            </div>
          ) : (
            <div className="mt-8 grid sm:grid-cols-2 gap-5">
              <Field label="Full name"><input data-testid="callback-name" className="input-exodus" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Your full name" /></Field>
              <Field label="Email"><input data-testid="callback-email" type="email" className="input-exodus" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@email.com" /></Field>
              <Field label="Phone (we'll call you here)"><input data-testid="callback-phone" type="tel" className="input-exodus" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="(555) 555-5555" /></Field>
              <Field label="Time window">
                <select data-testid="callback-slot" className="input-exodus" value={form.preferred_time_slot} onChange={(e) => setForm({ ...form, preferred_time_slot: e.target.value })}>
                  {TIME_SLOTS.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
                </select>
              </Field>
              <div className="col-span-full">
                <p className="text-xs eyebrow text-exodus-muted mb-3">Pick a date</p>
                <div className="grid grid-cols-2 sm:grid-cols-5 gap-2" data-testid="callback-date-grid">
                  {dates.map((d) => {
                    const iso = d.toISOString().slice(0, 10);
                    const selected = form.preferred_date === iso;
                    return (
                      <button key={iso} type="button" data-testid={`callback-date-${iso}`}
                        onClick={() => setForm({ ...form, preferred_date: iso })}
                        className={`px-3 py-3 text-xs border transition-colors ${selected ? "bg-exodus-gold text-exodus-bg border-exodus-gold" : "bg-exodus-bg text-exodus-text border-exodus-border hover:border-exodus-gold"}`}>
                        {_formatDateLabel(d)}
                      </button>
                    );
                  })}
                </div>
              </div>
              <Field label="What's this about? (optional)" full>
                <textarea data-testid="callback-topic" rows={3} className="input-exodus resize-none" value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} placeholder="Quick note so I know what to prep for the call…" />
              </Field>
              <div className="col-span-full">
                <button data-testid="callback-submit" onClick={submitCallback} disabled={submitting} className="btn-gold inline-flex items-center gap-3 disabled:opacity-40">
                  {submitting ? "Booking…" : "Add me to the callback list"}
                  {!submitting && <ArrowRight size={14} weight="bold" />}
                </button>
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-12 pb-16">
        <div className="card-surface p-8 lg:p-10">
          <div className="flex items-center gap-3 mb-6"><Clock size={20} className="text-exodus-gold" /><p className="eyebrow">Response times</p></div>
          <div className="grid sm:grid-cols-3 gap-6 text-sm">
            <div><p className="font-display text-2xl gold-text">Within 24 hrs</p><p className="text-exodus-muted mt-2">Email support Mon-Fri</p></div>
            <div><p className="font-display text-2xl gold-text">Within 48 hrs</p><p className="text-exodus-muted mt-2">Weekend and holiday email</p></div>
            <div><p className="font-display text-2xl gold-text">Same-day</p><p className="text-exodus-muted mt-2">Direct calls to {PHONE_DISPLAY}</p></div>
          </div>
        </div>
      </section>

      <section id="faq" className="max-w-5xl mx-auto px-6 lg:px-12 pb-24">
        <p className="eyebrow mb-3">Frequently asked</p>
        <h2 className="font-display text-3xl lg:text-4xl mb-10">Common questions.</h2>
        <div className="space-y-6">
          <Faq testid="faq-forgot-pin" q="I forgot my 4-digit PIN. How do I get back in?">Tap &quot;Not you? Use a different account&quot; on the PIN screen to start over. Then email support with your registered address and we&apos;ll reset your account.</Faq>
          <Faq testid="faq-account-required" q="Do I have to create an account to use the app?">Yes. A lightweight account (email + full name + 4-digit PIN) is required so your saved scenarios, calculator runs, quiz results, and uploaded policies are preserved across launches. No payment. No spam. No third-party sharing.</Faq>
          <Faq testid="faq-policy-upload" q="What happens when I upload my policy?">The app extracts the text from your PDF, runs it through an AI trained on insurance product analysis, and returns a plain-English breakdown of strengths, hidden costs, and next steps. Stored encrypted, only accessed by your advisor.</Faq>
          <Faq testid="faq-carrier-quotes" q="Are the carrier projections real quotes?">No. The Top-5 carrier match uses illustrative rating curves based on published industry norms. It is a directional education tool, not a bindable quote. For a bindable quote we run full underwriting.</Faq>
          <Faq testid="faq-consult-deposit" q="Why is there a $100 refundable deposit for a consult?">It filters serious inquiries and respects the advisor&apos;s time. The full deposit is refunded after the consult concludes. Free callback bookings above have no deposit.</Faq>
          <Faq testid="faq-data-tracking" q="Does the app track me?">The app collects your name, email, PIN, phone (if provided for callbacks), and in-app activity to power personalized recommendations. Not sold, not shared with data brokers, not used for third-party advertising. See our <Link to="/privacy" className="gold-text hover:underline">privacy policy</Link>.</Faq>
          <Faq testid="faq-app-crash" q="The app crashed or showed an error. What should I do?">Force-close and reopen. If the issue persists, email support with (1) the exact screen, (2) exact error text, (3) device model + iOS version. Include a screenshot if possible.</Faq>
          <Faq testid="faq-delete-account" q="How do I delete my account?">Email support@exoduslifeandwealth.com from your registered email with subject &quot;Delete account&quot;. We&apos;ll permanently remove your account and all data within 30 days.</Faq>
        </div>
      </section>

      <section className="max-w-5xl mx-auto px-6 lg:px-12 pb-24">
        <div className="border-t border-exodus-border pt-8 flex items-start gap-3 text-xs text-exodus-muted leading-relaxed">
          <MapPin size={14} className="text-exodus-gold shrink-0 mt-0.5" />
          <p>EXODUS Life &amp; Wealth is a private-wealth education practice. Insurance products are placed through licensed advisors and issued by A-rated carriers. Nothing on this app or website constitutes tax, legal, or investment advice. Consult a licensed professional before acting on any strategy.</p>
        </div>
      </section>
      <Footer />
    </div>
  );
}

function ContactCard({ testid, icon, title, primary, subtitle, href, internal }) {
  const Wrapper = internal ? Link : "a";
  const linkProps = internal ? { to: href } : { href };
  return (
    <Wrapper data-testid={testid} {...linkProps} className="card-surface p-8 flex flex-col group hover:border-exodus-gold transition-colors">
      <span className="text-exodus-gold mb-4">{icon}</span>
      <p className="eyebrow text-exodus-muted text-xs">{title}</p>
      <p className="font-display text-xl mt-2 group-hover:gold-text transition-colors">{primary}</p>
      <p className="text-sm text-exodus-muted leading-relaxed mt-3">{subtitle}</p>
      <span className="inline-flex items-center gap-2 text-xs eyebrow gold-text mt-5">Contact <ArrowRight size={12} weight="bold" /></span>
    </Wrapper>
  );
}
function Faq({ testid, q, children }) {
  return (
    <details data-testid={testid} className="card-surface p-6 group">
      <summary className="cursor-pointer flex items-center justify-between gap-4 text-base font-display list-none">
        <span>{q}</span><span className="text-exodus-gold text-lg group-open:rotate-45 transition-transform">+</span>
      </summary>
      <div className="text-sm text-exodus-muted leading-relaxed mt-4">{children}</div>
    </details>
  );
}
function Field({ label, full, children }) {
  return <div className={full ? "col-span-full" : ""}><p className="text-xs eyebrow text-exodus-muted mb-2">{label}</p>{children}</div>;
}
